import { useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth } from "@/components/auth/AuthContext";
import { inventoryCapabilities } from "@/lib/inventory/capabilities";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import {
  InventoryApiError,
  newInventoryKey,
  searchInventoryProducts,
  type InventoryProduct,
  type SerialMode,
} from "@/lib/inventory/api";
import { useInventoryText, type InventoryText } from "@/lib/inventory/i18n";

export const selectClass =
  "min-h-10 rounded border border-border bg-bg-surface px-3 py-2 text-sm text-fg-primary disabled:opacity-50";
export function useInventoryAccess() {
  const auth = useAuth();
  const authenticated = auth.isReady && auth.isAuthenticated;
  const capabilities = inventoryCapabilities(auth.groups);
  return {
    ready: auth.isReady,
    read: authenticated && capabilities.read,
    write: authenticated && capabilities.receive,
    supervisor: authenticated && capabilities.supervise,
  };
}
export function ErrorNotice({ error }: { error: unknown }) {
  const t = useInventoryText();
  if (!error) return null;
  const status = error instanceof InventoryApiError ? error.status : undefined;
  const text =
    status === 403 || status === 401
      ? t.denied
      : status === 503
        ? t.unavailable
        : status === 409
          ? t.conflict
          : t.error;
  return (
    <div
      role="alert"
      className="rounded border border-accent-rose/40 bg-accent-rose/10 p-3 text-sm text-accent-rose"
    >
      {text}
      {status && <span className="ml-2 font-mono">HTTP {status}</span>}
      {error instanceof InventoryApiError && error.detail && (
        <pre className="mt-1 whitespace-pre-wrap text-xs">{error.detail}</pre>
      )}
    </div>
  );
}
export function Loading() {
  const t = useInventoryText();
  return <p role="status">{t.loading}</p>;
}
export function Field({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="flex min-w-0 flex-col gap-1 text-sm text-fg-secondary">
      {label}
      {children}
    </label>
  );
}
export function Panel({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section
      aria-label={title}
      className="space-y-4 rounded border border-border bg-bg-surface p-4 sm:p-6"
    >
      <h2 className="text-lg font-510">{title}</h2>
      {children}
    </section>
  );
}
export function policyText(t: InventoryText, mode: SerialMode) {
  return (
    {
      REQUIRED: t.policyRequired,
      OPTIONAL: t.policyOptional,
      NONE: t.policyNone,
      UNCONFIGURED: t.policyUnconfigured,
    }[mode] || t.policyUnconfigured
  );
}
export function conditionText(t: InventoryText, condition: string) {
  return (
    (
      {
        AVAILABLE: t.available,
        QUARANTINED: t.quarantined,
        QUARANTINE: t.quarantined,
        QUARANTINE_PENDING_POLICY: t.pendingPolicy,
        QUARANTINE_PENDING_SERIALS: t.pendingSerials,
      } as Record<string, string>
    )[condition] || condition
  );
}
export function statusText(t: InventoryText, status: string) {
  return (
    (
      {
        UNASSIGNED: t.unassigned,
        ASSIGNED: t.assigned,
        VOIDED: t.statusVoided,
        QUARANTINE_PENDING_POLICY: t.pendingPolicy,
        QUARANTINE_PENDING_SERIALS: t.pendingSerials,
        QUARANTINED: t.statusQuarantined,
      } as Record<string, string>
    )[status] || status
  );
}
export function useRead<T>(load: () => Promise<T>, deps: unknown[]) {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(true);
  const [revision, setRevision] = useState(0);
  const requestId = useRef(0);
  useEffect(() => {
    let active = true;
    const id = ++requestId.current;
    setBusy(true);
    setError(null);
    setData(null);
    load()
      .then((result) => {
        if (active && requestId.current === id) setData(result);
      })
      .catch((e) => {
        if (active && requestId.current === id) setError(e);
      })
      .finally(() => {
        if (active && requestId.current === id) setBusy(false);
      });
    return () => {
      active = false;
    };
    // Callers explicitly provide all request dependencies.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, revision]);
  return {
    data,
    error,
    busy,
    refresh: () => {
      // Invalidate before the effect: no intermediate render of stale stock.
      ++requestId.current;
      setData(null);
      setError(null);
      setBusy(true);
      setRevision((v) => v + 1);
    },
  };
}
export function useInventoryWrite() {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [unresolved, setUnresolved] = useState(false);
  const lock = useRef(false);
  const identity = useRef({ payload: "", key: "" });
  const blocked =
    error instanceof InventoryApiError &&
    [401, 403, 409, 503].includes(error.status);
  async function run<T>(
    payload: unknown,
    operation: (key: string) => Promise<T>,
  ): Promise<T | undefined> {
    if (lock.current || blocked) return;
    const serialized = JSON.stringify(payload);
    if (serialized !== identity.current.payload)
      identity.current = { payload: serialized, key: newInventoryKey() };
    lock.current = true;
    setBusy(true);
    setUnresolved(true);
    setError(null);
    try {
      const result = await operation(identity.current.key);
      identity.current = { payload: "", key: "" };
      setUnresolved(false);
      return result;
    } catch (e) {
      setError(e);
      return undefined;
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  return {
    busy,
    error,
    blocked,
    unresolved,
    run,
    // Refreshing UI errors is not proof that an uncertain write did not commit.
    clear: () => setError(null),
  };
}
export function ProductPicker({
  onSelect,
  disabled = false,
}: {
  onSelect: (p: InventoryProduct) => void;
  disabled?: boolean;
}) {
  const t = useInventoryText();
  const [query, setQuery] = useState("");
  const { data, error, busy } = useRead(
    () => searchInventoryProducts(query),
    [query],
  );
  return (
    <div className="space-y-2">
      <Field label={t.search}>
        <Input
          type="search"
          placeholder={t.searchHint}
          value={query}
          disabled={disabled}
          onChange={(e) => setQuery(e.target.value)}
        />
      </Field>
      {busy && <Loading />}
      <ErrorNotice error={error} />
      {!busy && !error && (
        <ul className="max-h-48 overflow-auto rounded border border-border">
          {data?.products.map((p) => (
            <li key={p.product_id}>
              <Button
                variant="ghost"
                className="h-auto min-h-10 w-full justify-start whitespace-normal text-left"
                disabled={disabled || !p.stockable}
                onClick={() => onSelect(p)}
              >
                [{p.product_id}] {p.full_name || p.name} {p.ean}
                {!p.stockable && ` — ${t.notStockable}`}
              </Button>
            </li>
          ))}
          {data?.products.length === 0 && (
            <li className="p-3">{t.noMatches}</li>
          )}
        </ul>
      )}
    </div>
  );
}
export function Pager({
  offset,
  total,
  limit,
  onChange,
  busy,
}: {
  offset: number;
  total: number;
  limit: number;
  onChange: (n: number) => void;
  busy: boolean;
}) {
  const t = useInventoryText();
  return (
    <div className="flex flex-wrap items-center gap-3">
      <Button
        variant="secondary"
        disabled={busy || offset === 0}
        onClick={() => onChange(Math.max(0, offset - limit))}
      >
        {t.previous}
      </Button>
      <span>
        {t.total}: {total}
      </span>
      <Button
        variant="secondary"
        disabled={busy || offset + limit >= total}
        onClick={() => onChange(offset + limit)}
      >
        {t.next}
      </Button>
    </div>
  );
}
export function ConfirmReview({
  title,
  busy,
  blocked,
  onConfirm,
  onCancel,
  children,
}: {
  title: string;
  busy: boolean;
  blocked: boolean;
  onConfirm: () => void;
  onCancel: () => void;
  children: ReactNode;
}) {
  const t = useInventoryText();
  return (
    <section
      aria-label={title}
      className="space-y-3 rounded border border-accent-indigo p-4"
    >
      <h3 className="font-510">{title}</h3>
      {children}
      <div className="flex gap-3">
        <Button
          type="button"
          disabled={busy}
          variant="secondary"
          onClick={onCancel}
        >
          {t.cancel}
        </Button>
        <Button
          type="button"
          disabled={busy || blocked}
          isLoading={busy}
          onClick={onConfirm}
        >
          {t.confirm}
        </Button>
      </div>
    </section>
  );
}
